/**
 * hundredDisplay — Hundred-format display helpers.
 *
 * The Hundred has no "over" concept — everything is measured in balls.
 * The engine still tracks over_number + ball_in_over internally (5-ball
 * sets are how end-changes and bowler quotas are computed) but every
 * user-facing surface must present balls only.
 *
 * These helpers accept the whole matchType object so the caller can pass
 * whatever it already has (aggregate.match_type, props.matchType, etc.)
 * without having to guess which key holds `code` / `balls_per_over`.
 */

export function isHundred(matchType) {
  if (!matchType) return false;
  const code = (matchType.code || "").toString().toUpperCase();
  return code === "HUNDRED";
}

export function ballsPerOver(matchType) {
  const n = Number(matchType?.balls_per_over);
  return Number.isFinite(n) && n > 0 ? n : 6;
}

/**
 * Innings progress display.
 *   Hundred → "38 balls"
 *   Others  → "6.2"
 * Pass whichever of {legalBalls, completedOvers, ballInOver} you have —
 * the helper reconstructs the rest from balls_per_over.
 */
export function formatInningsProgress(matchType, { legalBalls, completedOvers, ballInOver } = {}) {
  const bpo = ballsPerOver(matchType);
  const totalBalls = legalBalls != null
    ? legalBalls
    : (completedOvers ?? 0) * bpo + (ballInOver ?? 0);
  if (isHundred(matchType)) return `${totalBalls} balls`;
  const ov = completedOvers != null ? completedOvers : Math.floor(totalBalls / bpo);
  const b = ballInOver != null ? ballInOver : totalBalls % bpo;
  return `${ov}.${b}`;
}

/**
 * Bowler quota / spell display.
 *   Hundred → "12 balls"  (max 20)
 *   Others  → "2.0"        (over.ball)
 */
export function formatBowlerBalls(matchType, balls) {
  const bpo = ballsPerOver(matchType);
  const n = Number(balls) || 0;
  if (isHundred(matchType)) return `${n}`;
  return `${Math.floor(n / bpo)}.${n % bpo}`;
}

/**
 * Column header for the bowler stats table.
 *   Hundred → "B"    (balls)
 *   Others  → "O"    (overs)
 */
export function bowlerBallsColumnLabel(matchType) {
  return isHundred(matchType) ? "B" : "O";
}

/**
 * Innings-cap label for chase / DLS chips.
 *   Hundred → "100 balls"
 *   Others  → "20 ov"
 */
export function formatMaxLength(matchType, maxOvers) {
  if (maxOvers == null) return null;
  if (isHundred(matchType)) return `${maxOvers * ballsPerOver(matchType)} balls`;
  return `${maxOvers} ov`;
}

/**
 * Ball-picker label (edit dialogs, ball-index lists).
 *   Hundred → "Ball 34"
 *   Others  → "3.4"       (0-indexed over . ball_in_over)
 * over_number is 1-indexed in the model; classical UI subtracts 1.
 */
export function formatBallReference(matchType, { overNumber, ballInOver, absoluteBallNumber }) {
  if (isHundred(matchType)) {
    if (absoluteBallNumber != null) return `Ball ${absoluteBallNumber}`;
    const bpo = ballsPerOver(matchType);
    const ov = Number(overNumber) || 1;
    const b = Number(ballInOver) || 0;
    return `Ball ${(ov - 1) * bpo + b}`;
  }
  const ov = (Number(overNumber) || 1) - 1;
  return `${ov}.${Number(ballInOver) || 0}`;
}

/**
 * Required-rate math for the ribbon.
 *   Hundred → { need, ballsLeft } — no per-over rate (irrelevant in a
 *             100-ball innings; runs-per-ball is the meaningful figure
 *             and the "Need N from B balls" line already conveys it).
 *   Others  → { need, ballsLeft, rrr } where rrr = need * 6 / ballsLeft.
 */
export function requiredRate(matchType, { need, ballsLeft }) {
  if (!Number.isFinite(need) || !Number.isFinite(ballsLeft) || ballsLeft <= 0) return null;
  if (isHundred(matchType)) return { need, ballsLeft };
  return { need, ballsLeft, rrr: (need * 6) / ballsLeft };
}
